/* @flow */

import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  getBraindatePath,
  getBraindateConversation,
  getBraindateLocation,
} from '@braindate/domain/lib/braindate/util';
import { getTopicPath } from '@braindate/domain/lib/topic/util';
import {
  getConversationParticipants,
  getConversationTopic,
} from '@braindate/domain/lib/conversation/util';
import { getUserId, getUserFirstName } from '@braindate/domain/lib/user/util';

import messages from 'plugin/virtual-braindate/l10n/virtualBraindateL10n';
import Notification from 'src/shared/app/notification/component/Notification';

import {
  isConversationAboutGroupBraindate,
  isConversationAboutFishbowl,
} from 'src/shared/domain/conversation/util/conversationUtil';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import { isVirtualLocation } from '@braindate/domain/lib/location/util';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { User } from '@braindate/domain/lib/user/type';

type Props = {
  braindate: Braindate,
  user: User,
  handleDismiss: () => void,
};

const VirtualBraindateEarlyReminderDumb = ({
  braindate,
  user,
  handleDismiss,
}: Props): React.Node => {
  const conversation = getBraindateConversation(braindate);
  const topic = getConversationTopic(conversation);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);
  const isFishbowl = isConversationAboutFishbowl(conversation);

  const location = getBraindateLocation(braindate);
  const isVirtual = location && isVirtualLocation(location);

  const href = isFishbowl ? getTopicPath(topic) : getBraindatePath(braindate);

  const participants = getConversationParticipants(conversation);
  const otherParticipants = participants.filter(
    (p) => getUserId(p) !== getUserId(user),
  );

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { braindateSingular } = useEventBraindateName();

  const messageTxt = isFishbowl
    ? intl.formatMessage(messages.earlyFishbowlReminder, {
        braindateSingular,
      })
    : intl.formatMessage(messages.earlyReminder, {
        isVirtual,
        isGroupBraindate,
        firstName: otherParticipants.map(getUserFirstName).join(', '),
        braindateSingular,
      });

  return (
    <Notification href={href} handleDismiss={handleDismiss}>
      {messageTxt}
    </Notification>
  );
};

export default React.memo<Props>(VirtualBraindateEarlyReminderDumb);
